import {
  Badge,
  Box,
  Button,
  Container,
  Grid,
  HStack,
  Icon,
  Text,
  VStack,
  useDisclosure
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  FiArrowLeft,
  FiCheck,
  FiCopy,
  FiInfo,
  FiLoader,
  FiZap
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import {
  mockApi,
  type CreateLinkData,
  type Link as LinkType
} from "../api/mockApi";
import {
  AdvancedSettings,
  CampaignDetails,
  DestinationSection,
  DomainSelection,
  FilteredDestinationSection,
  LinkConfiguration,
  LinkPreview,
  PlatformSelection,
  type DestinationConfig
} from "../components/CreateLink";

interface FormData {
  platform: string;
  redirectUrl: string;
  linkName: string;
  adSetName: string;
  adName: string;
  customId: string;
  metadata: string;
  forceExternalBrowser: boolean;
  domain: string;
  customQueryParams: string;
  // Legacy destination fields (for backward compatibility)
  destinationType: "single" | "ab-test" | "variant" | "custom";
  destinationPath: string;
  destinationPathA: string;
  destinationPathB: string;
  abTestSplit: number;
  destinationVariant: string;
  customRedirectUrl: string;
  abTestPaths: Array<{
    id: string;
    type: "path" | "variant";
    value: string;
    percentage: number;
  }>;
  abTestSchedule: {
    enabled: boolean;
    startDate: string;
    endDate: string;
  };
  // New filtered destinations
  destinations: DestinationConfig[];
  useFilteredDestinations: boolean;
}

export default function CreateLink() {
  const [formData, setFormData] = useState<FormData>({
    platform: "",
    redirectUrl: "",
    linkName: "",
    adSetName: "",
    adName: "",
    customId: "",
    metadata: "",
    forceExternalBrowser: false,
    domain: "try.zown.ca",
    customQueryParams: "",
    destinationType: "single",
    destinationPath: "",
    destinationPathA: "",
    destinationPathB: "",
    abTestSplit: 50,
    destinationVariant: "",
    customRedirectUrl: "",
    abTestPaths: [
      { id: "1", type: "path", value: "", percentage: 50 },
      { id: "2", type: "path", value: "", percentage: 50 }
    ],
    abTestSchedule: {
      enabled: false,
      startDate: "",
      endDate: ""
    },
    // New filtered destinations with default
    destinations: [
      {
        id: "default",
        isDefault: true,
        filters: {},
        destinationType: "single",
        destinationPath: "",
        abTestPaths: [
          { id: "1", type: "path", value: "", percentage: 50 },
          { id: "2", type: "path", value: "", percentage: 50 }
        ],
        isExpanded: true
      }
    ],
    useFilteredDestinations: true // Default to using the new system
  });

  const [customIdAvailable, setCustomIdAvailable] = useState<boolean | null>(
    null
  );
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [createdLink, setCreatedLink] = useState<LinkType | null>(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const { open, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fixed A/B test link ID
  const AB_TEST_LINK_ID = "1764605800";

  // Generate the full URL for a link
  const getLinkUrl = (id: string, domain: string = "try.zown.ca", isABTest: boolean = false) => {
    // For A/B tests, always use the fixed link ID
    if (isABTest) {
      return `https://try.zown.ca/${AB_TEST_LINK_ID}`;
    }
    return `https://${domain}/${id}`;
  };

  const createLinkMutation = useMutation({
    mutationFn: mockApi.createLink,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["links"] });
      setCreatedLink(data);
      onOpen();

      // Check if this is an A/B test
      const isABTest = formData.useFilteredDestinations
        ? formData.destinations.some(d => d.destinationType === "ab-test")
        : formData.destinationType === "ab-test";

      // Auto-copy to clipboard
      const linkUrl = getLinkUrl(
        data.customId || data.id || "",
        formData.domain,
        isABTest
      );
      navigator.clipboard.writeText(linkUrl);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let linkData: CreateLinkData;

    if (formData.useFilteredDestinations) {
      // Validate destinations
      const defaultDestination = formData.destinations.find((d) => d.isDefault);
      if (!defaultDestination) {
        setMetadataError("At least one default destination is required");
        return;
      }

      // Validate each destination's custom URL if needed
      for (const dest of formData.destinations) {
        if (dest.destinationType === "custom") {
          if (!dest.customRedirectUrl) {
            setMetadataError(
              "Custom redirect URL is required for custom destinations"
            );
            return;
          }

          try {
            const url = new URL(dest.customRedirectUrl);
            if (!["http:", "https:"].includes(url.protocol)) {
              setMetadataError(
                "Custom URL must start with http:// or https://"
              );
              return;
            }
          } catch (error) {
            setMetadataError("Invalid custom redirect URL format");
            return;
          }
        }
      }

      // Use the default destination for the primary link data
      // In a real implementation, you'd send all destinations to the backend
      linkData = {
        platform: formData.platform,
        redirectUrl: "",
        linkName: formData.linkName || undefined,
        adSetName: formData.adSetName || undefined,
        adName: formData.adName || undefined,
        customId: formData.customId || undefined,
        metadata: formData.metadata ? JSON.parse(formData.metadata) : undefined,
        forceExternalBrowser: formData.forceExternalBrowser,
        domain: formData.domain,
        customQueryParams: formData.customQueryParams || undefined,
        destinationType: defaultDestination.destinationType,
        destinationPath:
          defaultDestination.destinationType === "single"
            ? defaultDestination.destinationPath
            : undefined,
        destinationVariant:
          defaultDestination.destinationType === "variant"
            ? defaultDestination.destinationVariant
            : undefined,
        customRedirectUrl:
          defaultDestination.destinationType === "custom"
            ? defaultDestination.customRedirectUrl
            : undefined,
        abTestPaths:
          defaultDestination.destinationType === "ab-test"
            ? defaultDestination.abTestPaths
            : undefined,
        abTestSchedule:
          defaultDestination.destinationType === "ab-test"
            ? defaultDestination.abTestSchedule
            : undefined,
        // Store full destinations array in metadata for now
        // In a real implementation, this would be a separate field
        destinations: formData.destinations
      } as any; // Type assertion needed for destinations field
    } else {
      // Legacy validation
      if (formData.destinationType === "custom") {
        if (!formData.customRedirectUrl) {
          setMetadataError("Custom redirect URL is required");
          return;
        }

        try {
          const url = new URL(formData.customRedirectUrl);
          if (!["http:", "https:"].includes(url.protocol)) {
            setMetadataError("Custom URL must start with http:// or https://");
            return;
          }
        } catch (error) {
          setMetadataError("Invalid custom redirect URL format");
          return;
        }
      }

      linkData = {
        platform: formData.platform,
        redirectUrl: "",
        linkName: formData.linkName || undefined,
        adSetName: formData.adSetName || undefined,
        adName: formData.adName || undefined,
        customId: formData.customId || undefined,
        metadata: formData.metadata ? JSON.parse(formData.metadata) : undefined,
        forceExternalBrowser: formData.forceExternalBrowser,
        domain: formData.domain,
        customQueryParams: formData.customQueryParams || undefined,
        destinationType: formData.destinationType,
        destinationPath:
          formData.destinationType === "single"
            ? formData.destinationPath
            : undefined,
        destinationPathA:
          formData.destinationType === "ab-test"
            ? formData.destinationPathA
            : undefined,
        destinationPathB:
          formData.destinationType === "ab-test"
            ? formData.destinationPathB
            : undefined,
        abTestSplit:
          formData.destinationType === "ab-test"
            ? formData.abTestSplit
            : undefined,
        destinationVariant:
          formData.destinationType === "variant"
            ? formData.destinationVariant
            : undefined,
        abTestPaths:
          formData.destinationType === "ab-test"
            ? formData.abTestPaths
            : undefined,
        abTestSchedule:
          formData.destinationType === "ab-test"
            ? formData.abTestSchedule
            : undefined
      };
    }

    // Validate metadata if provided
    if (formData.metadata) {
      try {
        JSON.parse(formData.metadata);
      } catch (error) {
        setMetadataError("Invalid JSON format");
        return;
      }
    }

    createLinkMutation.mutate(linkData);
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCampaignChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMetadataChange = (value: string, error: string | null) => {
    setFormData((prev) => ({ ...prev, metadata: value }));
    setMetadataError(error);
  };

  const handleCustomIdChange = (value: string, isAvailable: boolean | null) => {
    setFormData((prev) => ({ ...prev, customId: value }));
    setCustomIdAvailable(isAvailable);
  };

  const handleCreateNew = () => {
    // Reset form
    setFormData({
      platform: "",
      redirectUrl: "",
      linkName: "",
      adSetName: "",
      adName: "",
      customId: "",
      metadata: "",
      forceExternalBrowser: false,
      domain: "try.zown.ca",
      customQueryParams: "",
      destinationType: "single",
      destinationPath: "",
      destinationPathA: "",
      destinationPathB: "",
      abTestSplit: 50,
      destinationVariant: "",
      customRedirectUrl: "",
      abTestPaths: [
        { id: "1", type: "path", value: "", percentage: 50 },
        { id: "2", type: "path", value: "", percentage: 50 }
      ],
      abTestSchedule: {
        enabled: false,
        startDate: "",
        endDate: ""
      },
      destinations: [
        {
          id: "default",
          isDefault: true,
          filters: {},
          destinationType: "single",
          destinationPath: "",
          abTestPaths: [
            { id: "1", type: "path", value: "", percentage: 50 },
            { id: "2", type: "path", value: "", percentage: 50 }
          ],
          isExpanded: true
        }
      ],
      useFilteredDestinations: true
    });
    setMetadataError(null);
    setCreatedLink(null);
    setCopiedToClipboard(false);
    onClose();
  };

  const handleCreateSimilar = () => {
    // Close the modal
    onClose();

    // Reset only the ad name, link name, and custom ID (keep other fields including metadata)
    setFormData((prev) => ({
      ...prev,
      linkName: prev.linkName + " - Copy",
      adName: "", // Clear ad name so user can enter a new one
      customId: "" // Clear custom ID since it must be unique
    }));
    setCustomIdAvailable(null);
    setMetadataError(null);

    // Reset states
    setCreatedLink(null);
    setCopiedToClipboard(false);

    // Focus on the ad name field after a short delay
    setTimeout(() => {
      const adNameInput = document.querySelector(
        'input[name="adName"]'
      ) as HTMLInputElement;
      if (adNameInput) {
        adNameInput.focus();
        adNameInput.select();
      }
    }, 100);
  };

  const handleGoToLinks = () => {
    navigate("/links");
  };

  const copyToClipboard = () => {
    if (createdLink) {
      // Check if this is an A/B test
      const isABTest = formData.useFilteredDestinations
        ? formData.destinations.some(d => d.destinationType === "ab-test")
        : formData.destinationType === "ab-test";

      const linkUrl = getLinkUrl(
        createdLink.customId || createdLink.id || "",
        formData.domain,
        isABTest
      );
      navigator.clipboard.writeText(linkUrl);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    }
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="6xl" py={8}>
        {/* Header */}
        <VStack gap={6} align="stretch" mb={8}>
          <HStack justify="space-between" align="center">
            <HStack gap={3}>
              <Link to="/links">
                <Button variant="ghost" size="sm" _hover={{ bg: "gray.100" }}>
                  <FiArrowLeft />
                  Back to Links
                </Button>
              </Link>
              <Text color="gray.500">/</Text>
              <Text color="gray.700" fontWeight="medium">
                Create New Link
              </Text>
            </HStack>
            <Badge colorScheme="blue" px={3} py={1} rounded="full">
              <HStack gap={1}>
                <Icon as={FiZap} />
                <Text>Quick Setup</Text>
              </HStack>
            </Badge>
          </HStack>
        </VStack>

        <Grid templateColumns={{ base: "1fr", lg: "1fr 400px" }} gap={8}>
          {/* Main Form */}
          <VStack as="form" onSubmit={handleSubmit} gap={6} align="stretch">
            {/* Domain Selection */}
            <DomainSelection
              domain={formData.domain}
              onChange={(domain) => handleFieldChange("domain", domain)}
              disabled={createLinkMutation.isPending}
            />

            {/* Platform Selection */}
            <PlatformSelection
              platform={formData.platform}
              onChange={(platform) => handleFieldChange("platform", platform)}
              disabled={createLinkMutation.isPending}
            />

            {/* Campaign Details */}
            <CampaignDetails
              linkName={formData.linkName}
              adSetName={formData.adSetName}
              adName={formData.adName}
              onChange={handleCampaignChange}
              disabled={createLinkMutation.isPending}
            />

            {/* Destination - New Filtered System */}
            {formData.useFilteredDestinations ? (
              <FilteredDestinationSection
                domain={formData.domain}
                destinations={formData.destinations}
                onChange={(destinations) =>
                  handleFieldChange("destinations", destinations)
                }
                disabled={createLinkMutation.isPending}
              />
            ) : (
              /* Legacy Destination Section (for backward compatibility) */
              <DestinationSection
                domain={formData.domain}
                destinationType={formData.destinationType}
                destinationPath={formData.destinationPath}
                destinationVariant={formData.destinationVariant}
                customRedirectUrl={formData.customRedirectUrl}
                abTestPaths={formData.abTestPaths}
                abTestSchedule={formData.abTestSchedule}
                onChange={handleFieldChange}
                disabled={createLinkMutation.isPending}
              />
            )}

            {/* Link Configuration */}
            <LinkConfiguration
              redirectUrl={formData.redirectUrl}
              customId={formData.customId}
              onRedirectUrlChange={(value) =>
                handleFieldChange("redirectUrl", value)
              }
              onCustomIdChange={handleCustomIdChange}
              disabled={createLinkMutation.isPending}
            />

            {/* Advanced Settings */}
            <AdvancedSettings
              metadata={formData.metadata}
              metadataError={metadataError}
              forceExternalBrowser={formData.forceExternalBrowser}
              customQueryParams={formData.customQueryParams}
              redirectUrl={formData.redirectUrl}
              onMetadataChange={handleMetadataChange}
              onForceExternalBrowserChange={(value) =>
                handleFieldChange("forceExternalBrowser", value)
              }
              onCustomQueryParamsChange={(value) =>
                handleFieldChange("customQueryParams", value)
              }
              disabled={createLinkMutation.isPending}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              disabled={
                createLinkMutation.isPending ||
                !formData.platform ||
                customIdAvailable === false ||
                !!metadataError
              }
              width="full"
            >
              <HStack gap={2}>
                {createLinkMutation.isPending ? (
                  <>
                    <Icon as={FiLoader} className="animate-spin" />
                    <Text>Creating Link...</Text>
                  </>
                ) : (
                  <>
                    <Icon as={FiZap} />
                    <Text>Create Link</Text>
                  </>
                )}
              </HStack>
            </Button>
          </VStack>

          {/* Sidebar */}
          <VStack gap={6} align="stretch">
            {/* Link Preview */}
            <LinkPreview
              platform={formData.platform}
              linkName={formData.linkName}
              adSetName={formData.adSetName}
              adName={formData.adName}
              destinationType={
                formData.destinationType === "custom"
                  ? "single"
                  : (formData.destinationType as
                      | "single"
                      | "ab-test"
                      | "variant")
              }
              destinationPath={formData.destinationPath}
              destinationVariant={formData.destinationVariant}
              abTestPaths={formData.abTestPaths}
              abTestSchedule={formData.abTestSchedule}
              forceExternalBrowser={formData.forceExternalBrowser}
              customQueryParams={formData.customQueryParams}
              generatedUrl={
                // Check if A/B test is selected
                (formData.useFilteredDestinations
                  ? formData.destinations.some(d => d.destinationType === "ab-test")
                  : formData.destinationType === "ab-test")
                  ? `https://try.zown.ca/${AB_TEST_LINK_ID}`
                  : formData.customId
                    ? getLinkUrl(formData.customId, formData.domain)
                    : `https://${formData.domain}/[auto-generated]`
              }
              domain={formData.domain}
              customId={formData.customId}
              redirectUrl={formData.redirectUrl}
            />

            {/* Tips Card */}
            <Box
              bg="blue.50"
              shadow="sm"
              borderColor="blue.200"
              border="1px"
              rounded="lg"
              p={6}
            >
              <HStack mb={3}>
                <Icon as={FiInfo} color="blue.600" boxSize={5} />
                <Text fontWeight="semibold" color="blue.900">
                  Pro Tips
                </Text>
              </HStack>

              <VStack align="stretch" gap={2} fontSize="sm" color="blue.800">
                <Text>
                  • Use consistent naming conventions for easy filtering
                </Text>
                <Text>
                  • Include date ranges in campaign names when relevant
                </Text>
                <Text>
                  • Add platform-specific identifiers to track performance
                </Text>
                <Text>• Keep ad names short but descriptive</Text>
              </VStack>
            </Box>
          </VStack>
        </Grid>

        {/* Success Dialog */}
        {open && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={9999}
          >
            <Box
              bg="white"
              rounded="lg"
              shadow="xl"
              p={8}
              maxW="md"
              width="90%"
            >
              <VStack gap={4} align="center">
                <Box
                  bg="green.100"
                  rounded="full"
                  p={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiCheck} color="green.600" boxSize={8} />
                </Box>
                <Text fontSize="2xl" fontWeight="bold">
                  Link Created Successfully!
                </Text>
                <Text color="gray.600" textAlign="center">
                  Your tracking link has been created and is ready to use.
                </Text>

                {createdLink && (
                  <Box
                    bg="gray.50"
                    p={4}
                    rounded="md"
                    width="100%"
                    border="1px"
                    borderColor="gray.200"
                  >
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      Your tracking link:
                    </Text>
                    <HStack gap={2}>
                      <Text
                        fontFamily="mono"
                        fontSize="sm"
                        flex={1}
                        wordBreak="break-all"
                      >
                        {getLinkUrl(
                          createdLink.customId || createdLink.id || "",
                          formData.domain,
                          formData.useFilteredDestinations
                            ? formData.destinations.some(d => d.destinationType === "ab-test")
                            : formData.destinationType === "ab-test"
                        )}
                      </Text>
                      <Button size="sm" onClick={copyToClipboard}>
                        <HStack gap={1}>
                          <Icon as={FiCopy} />
                          <Text>{copiedToClipboard ? "Copied!" : "Copy"}</Text>
                        </HStack>
                      </Button>
                    </HStack>
                  </Box>
                )}

                <HStack gap={3} width="full" mt={6}>
                  <Button
                    colorScheme="blue"
                    onClick={handleCreateSimilar}
                    flex={1}
                  >
                    Create Similar
                  </Button>
                  <Button variant="outline" onClick={handleCreateNew} flex={1}>
                    Create New
                  </Button>
                  <Button variant="ghost" onClick={handleGoToLinks}>
                    View All Links
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}

// Add spinning animation
const style = document.createElement("style");
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }
`;
document.head.appendChild(style);
